import { useCallback, useEffect, useRef, useState } from 'react';
import { useTimeTracker } from '../context/TimeTrackerContext';
import { Issue } from '../types';

// Column definitions for sorting
const SORTABLE_COLUMNS = ['key', 'summary', 'status'] as const;

/**
 * Escape special characters in filter values to prevent injection
 * Escapes quotes and backslashes for AIP-160 filter syntax
 */
const escapeFilterValue = (value: string): string => {
  return value
    .replace(/\\/g, '\\\\')  // Escape backslashes first
    .replace(/"/g, '\\"');    // Escape double quotes
};

/**
 * Build AIP-160 filter string for backend
 */
export const buildFilterString = (
  search: string,
  statuses: string[],
  excludedStatuses: string[] = [],
  assigneeId?: string
): string => {
  const parts: string[] = [];

  if (search) {
    if (search.includes('*') || search.includes('=')) {
      // Advanced filter: user provides AIP-160 expression directly (e.g. key="*value")
      parts.push(`(${search})`);
    } else {
      // Simple search: wrap in wildcards for contains-match on key and summary
      const escapedSearch = escapeFilterValue(search);
      parts.push(`(key:"*${escapedSearch}*" OR summary:"*${escapedSearch}*")`);
    }
  }

  if (statuses.length > 0) {
    const statusFilters = statuses.map(s => `status="${escapeFilterValue(s)}"`).join(' OR ');
    parts.push(`(${statusFilters})`);
  }

  if (excludedStatuses.length > 0) {
    const excludeFilters = excludedStatuses.map(s => `status!="${escapeFilterValue(s)}"`).join(' AND ');
    parts.push(`(${excludeFilters})`);
  }

  if (assigneeId) {
    parts.push(`assigneeId="${escapeFilterValue(assigneeId)}"`);
  }

  return parts.join(' AND ');
};

/**
 * Build orderBy string for backend sorting
 */
export const buildOrderBy = (sortIndex: number | null, sortDirection: 'asc' | 'desc'): string | undefined => {
  if (sortIndex === null) return undefined;
  const column = SORTABLE_COLUMNS[sortIndex];
  return `${column} ${sortDirection}`;
};

interface UseIssuesDataParams {
  searchValue: string;
  selectedStatuses: string[];
  selectedLabels?: string[];
  excludedStatuses?: string[];
  assigneeId?: string;
  perPage: number;
  activeSortIndex: number | null;
  activeSortDirection: 'asc' | 'desc';
  enabled?: boolean;
}

interface UseIssuesDataReturn {
  issues: Issue[];
  totalSize: number;
  loading: boolean;
  error: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToFirstPage: () => void;
  /** 1-based display offset of the first item on current page */
  pageStartIndex: number;
  retry: () => void;
}

/**
 * Hook that manages issues data fetching with cursor-based (keyset) pagination,
 * filtering, and sorting. Supports next/previous/first navigation only.
 */
export const useIssuesData = ({
  searchValue,
  selectedStatuses,
  selectedLabels = [],
  excludedStatuses = [],
  assigneeId,
  perPage,
  activeSortIndex,
  activeSortDirection,
  enabled = true,
}: UseIssuesDataParams): UseIssuesDataReturn => {
  const { fetchIssues, issuesLoading, issuesError } = useTimeTracker();

  const [issues, setIssues] = useState<Issue[]>([]);
  const [totalSize, setTotalSize] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cursor-based pagination state
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>(undefined);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [prevTokenStack, setPrevTokenStack] = useState<(string | undefined)[]>([]);

  // Track request ID to handle race conditions
  const requestIdRef = useRef(0);
  // StrictMode double-mount protection
  const fetchedParamsRef = useRef<string>('');

  const fetchPage = useCallback(async (
    pageToken: string | undefined,
    pageSize: number,
    filter: string,
    orderBy?: string,
    labels?: string[]
  ) => {
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchIssues({
        pageSize,
        pageToken,
        filter: filter || undefined,
        orderBy,
        labels: labels && labels.length > 0 ? labels : undefined,
      });

      if (currentRequestId !== requestIdRef.current) return;

      setIssues(result.items);
      setTotalSize(result.totalSize ?? 0);
      setNextPageToken(result.nextPageToken ?? null);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;

      // Handle invalid/expired token — auto-retry from page 1
      const errorMsg = err instanceof Error ? err.message : '';
      if (errorMsg.includes('400') || errorMsg.includes('page token') || errorMsg.includes('pagination')) {
        setCurrentPageToken(undefined);
        setPrevTokenStack([]);
        setNextPageToken(null);
        try {
          const retryResult = await fetchIssues({
            pageSize,
            filter: filter || undefined,
            orderBy,
            labels: labels && labels.length > 0 ? labels : undefined,
          });
          if (currentRequestId !== requestIdRef.current) return;
          setIssues(retryResult.items);
          setTotalSize(retryResult.totalSize ?? 0);
          setNextPageToken(retryResult.nextPageToken ?? null);
        } catch (retryErr) {
          if (currentRequestId !== requestIdRef.current) return;
          setError(retryErr instanceof Error ? retryErr.message : 'Failed to load issues');
        }
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load issues');
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [fetchIssues]);

  // Build current filter/sort params
  const filter = buildFilterString(searchValue, selectedStatuses, excludedStatuses, assigneeId);
  const orderBy = buildOrderBy(activeSortIndex, activeSortDirection);
  const labelsParam = selectedLabels.length > 0 ? selectedLabels : undefined;

  // Reset pagination when filter/sort/perPage changes
  const filterKeyRef = useRef('');
  const filterKey = `${filter}|${orderBy}|${selectedLabels.join(',')}|${perPage}`;

  useEffect(() => {
    if (filterKeyRef.current && filterKeyRef.current !== filterKey) {
      setCurrentPageToken(undefined);
      setPrevTokenStack([]);
      setNextPageToken(null);
    }
    filterKeyRef.current = filterKey;
  }, [filterKey]);

  // Fetch data when params change
  useEffect(() => {
    if (!enabled) return;
    const paramsKey = `${filterKey}|${currentPageToken ?? 'first'}`;

    if (fetchedParamsRef.current === paramsKey) return;
    fetchedParamsRef.current = paramsKey;

    fetchPage(currentPageToken, perPage, filter, orderBy, labelsParam);
  }, [enabled, currentPageToken, perPage, filter, orderBy, labelsParam, filterKey, fetchPage]);

  // Navigation methods
  const goToNextPage = useCallback(() => {
    if (nextPageToken) {
      setPrevTokenStack((prev) => [...prev, currentPageToken]);
      setCurrentPageToken(nextPageToken);
    }
  }, [nextPageToken, currentPageToken]);

  const goToPreviousPage = useCallback(() => {
    setPrevTokenStack((prev) => {
      const newStack = [...prev];
      const prevToken = newStack.pop();
      setCurrentPageToken(prevToken);
      return newStack;
    });
  }, []);

  const goToFirstPage = useCallback(() => {
    setCurrentPageToken(undefined);
    setPrevTokenStack([]);
  }, []);

  const retry = useCallback(() => {
    fetchedParamsRef.current = '';
    fetchPage(currentPageToken, perPage, filter, orderBy, labelsParam);
  }, [currentPageToken, perPage, filter, orderBy, labelsParam, fetchPage]);

  const pageStartIndex = prevTokenStack.length * perPage + 1;

  return {
    issues,
    totalSize,
    loading: loading || issuesLoading,
    error: error || issuesError,
    hasNextPage: nextPageToken !== null,
    hasPreviousPage: prevTokenStack.length > 0,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    pageStartIndex,
    retry,
  };
};
